import { BlogError, Footer, GradientBg, Header } from "../../components"
import { api } from "../../ghostClient"
import { ArrowLeftIcon } from "@heroicons/react/outline"
import { PostOrPage } from "@tryghost/content-api"
import { format } from "date-fns"
import { GetStaticProps } from "next"
import { NextSeo } from "next-seo"
import Link from "next/link"
import { useRouter } from "next/router"
import React from "react"

const TwitterIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="text-gray-400 group-hover:text-primary"
  >
    <path
      d="M19.6169 0.00896646C18.763 0.611275 17.8176 1.07195 16.8171 1.37324C16.28 0.755757 15.5663 0.318104 14.7725 0.119469C13.9786 -0.0791667 13.1429 -0.0292013 12.3784 0.262607C11.6138 0.554416 10.9573 1.07399 10.4977 1.75105C10.0381 2.42812 9.7975 3.23001 9.80847 4.04827V4.93995C8.24146 4.98058 6.68873 4.63305 5.28855 3.92829C3.88838 3.22354 2.68424 2.18345 1.78336 0.900645C1.78336 0.900645 -1.78336 8.92575 6.24175 12.4925C4.40536 13.739 2.21775 14.364 0 14.2758C8.02511 18.7342 17.8336 14.2758 17.8336 4.02152C17.8328 3.77315 17.8089 3.52539 17.7622 3.28143C18.6723 2.38395 19.3145 1.25082 19.6169 0.00896646Z"
      fill="currentColor"
    />
  </svg>
)
const FacebookIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="text-gray-400 group-hover:text-primary"
  >
    <path
      d="M10.0001 0.041626C4.47724 0.041626 0 4.51886 0 10.0417C0 14.995 3.60522 19.0972 8.33225 19.8916V12.128H5.91994V9.33417H8.33225V7.27413C8.33225 4.88389 9.79213 3.58135 11.9247 3.58135C12.9461 3.58135 13.8238 3.65746 14.0786 3.69098V6.18942L12.5995 6.19013C11.44 6.19013 11.2165 6.74102 11.2165 7.54969V9.33274H13.9832L13.6223 12.1265H11.2165V19.9584C16.1642 19.3562 20 15.1495 20 10.0388C20 4.51886 15.5228 0.041626 10.0001 0.041626Z"
      fill="currentColor"
    ></path>
  </svg>
)
const LinkedinIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="text-gray-400 group-hover:text-primary"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M16.338 16.338H13.67V12.16C13.67 11.165 13.653 9.883 12.283 9.883C10.893 9.883 10.682 10.969 10.682 12.09V16.338H8.014V7.748H10.573V8.922H10.61C10.966 8.247 11.837 7.535 13.136 7.535C15.839 7.535 16.339 9.313 16.339 11.627V16.338H16.338ZM5.005 6.575C4.80171 6.5752 4.60038 6.53535 4.41249 6.45774C4.2246 6.38013 4.05384 6.26627 3.90996 6.12266C3.76607 5.97906 3.65188 5.80852 3.57391 5.62078C3.49593 5.43304 3.4557 5.23179 3.4555 5.0285C3.4553 4.82521 3.49515 4.62388 3.57276 4.43599C3.65037 4.2481 3.76423 4.07734 3.90784 3.93346C4.05144 3.78957 4.22198 3.67538 4.40972 3.59741C4.59746 3.51943 4.79871 3.4792 5.002 3.479C5.41255 3.4786 5.80645 3.64131 6.09704 3.93134C6.38763 4.22136 6.5511 4.61494 6.5515 5.0255C6.5519 5.43606 6.38919 5.82995 6.09916 6.12054C5.80914 6.41113 5.41556 6.5746 5.005 6.575ZM3.668 16.338H6.34V7.748H3.667V16.338H3.668ZM17.668 1H2.328C1.595 1 1 1.581 1 2.298V17.701C1 18.418 1.595 19 2.328 19H17.668C18.402 19 19 18.418 19 17.701V2.298C19 1.581 18.402 1 17.668 1Z"
      fill="currentColor"
    ></path>
  </svg>
)

interface Props {
  post: PostOrPage
  hasError?: boolean
}

export default function BlogPost({ post, hasError }: Props) {
  const router = useRouter()
  const canonicalUrl = (
    `https://www.coverdash.com` + (router.asPath === "/" ? "" : router.asPath)
  ).split("?")[0]

  return (
    <>
      <NextSeo
        title={post.title}
        description={post.excerpt}
        canonical={canonicalUrl}
        openGraph={{
          url: canonicalUrl,
          title: post.title,
          description: post.excerpt,
          images: [
            {
              url:
                post.feature_image ||
                "https://coverdash.s3.amazonaws.com/logo/coverdash_social_preview_800x.jpg",
              width: 800,
              height: 418,
              alt:
                post.feature_image_alt ||
                "Coverdash - Business insurance in a dash.",
            },
            {
              url:
                post.feature_image ||
                "https://coverdash.s3.amazonaws.com/logo/coverdash_social_preview_800x.jpg",
              width: 1200,
              height: 627,
              alt:
                post.feature_image_alt ||
                "Coverdash - Business insurance in a dash.",
            },
          ],
        }}
        twitter={{
          handle: "@coverdash_inc",
          site: "@coverdash_inc",
          cardType: "summary_large_image",
        }}
      />
      <div className="overflow-hidden">
        <Header />
        <GradientBg />

        {hasError ? (
          <BlogError />
        ) : (
          <div className="mt-20 py-16 px-4 sm:px-6 lg:px-8">
            <div className="max-w-prose mx-auto prose">
              <div className="sm:flex items-center sm:justify-between">
                <div>
                  <Link legacyBehavior href="/blog">
                    <a className="group">
                      <ArrowLeftIcon className="text-gray-500 h-5 w-5 group-hover:text-primary" />
                    </a>
                  </Link>
                </div>
                <h1 className="-ml-5 mb-0 block text-5xl text-center font-extrabold tracking-tight text-alt sm:text-4xl max-w-xl">
                  {post.title}
                </h1>
                <div className="flex-0" />
              </div>
              {post.tags && post.tags.length > 0 && (
                <ul className="block mt-10 list-none p-0">
                  {post?.tags.map((tag) => (
                    <li key={tag.id} className="p-0 mr-2">
                      <Link legacyBehavior href={`/blog/tags/${tag.slug}`}>
                        <a className="text-gray-500 border border-primary px-2 py-1 font-medium text-sm rounded-md hover:bg-primary hover:text-white cursor-pointer no-underline">
                          {tag.name}
                        </a>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
              {post.primary_author && post.published_at && (
                <div className="mt-4 sm:mt-6 sm:flex items-center sm:justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-900 my-0">
                      {post.primary_author?.name}
                    </p>
                    <div className="flex space-x-1 text-sm text-gray-500">
                      <time dateTime={post.published_at}>
                        {format(new Date(post.published_at), "MMM d, yyyy")}
                      </time>
                    </div>
                  </div>
                  <div className="flex space-x-2 mt-3 sm:mt-0">
                    <a
                      href={`https://www.facebook.com/sharer/sharer.php?u=https://coverdash.com/${router.asPath}&title=${post.title}-Coverdash`}
                      className="py-2.5 px-5 group rounded-md border border-gray-400 h-full flex justify-center items-center"
                    >
                      <FacebookIcon />
                    </a>
                    <a
                      href={`https://twitter.com/intent/tweet?url=https://coverdash.com/${router.asPath}&text=${post.title}-Coverdash`}
                      className="py-2.5 px-5 group rounded-md border border-gray-400 h-full flex justify-center items-center"
                    >
                      <TwitterIcon />
                    </a>
                    <a
                      href={`https://www.linkedin.com/shareArticle?mini=true&url=https://www.coverdash.com/${router.asPath}&title=${post.title}-Coverdash`}
                      className="py-2.5 px-5 group rounded-md border border-gray-400 h-full flex justify-center items-center"
                    >
                      <LinkedinIcon />
                    </a>
                  </div>
                </div>
              )}
            </div>
            <div className="max-w-prose prose mx-auto">
              {post.feature_image && (
                <img
                  src={post.feature_image}
                  className="w-full rounded-lg mt-6"
                  alt="featured image"
                />
              )}

              {post.html && (
                <div
                  className="mt-6 text-gray-500 prose-a:primary prose-img:rounded-lg"
                  dangerouslySetInnerHTML={{ __html: post.html }}
                />
              )}
            </div>
          </div>
        )}
        <Footer />
      </div>
    </>
  )
}

export const getStaticProps: GetStaticProps = async (context) => {
  const slug = context.params?.slug as string
  const post = await api.posts
    .read(
      { slug },
      {
        include: ["tags", "authors"],
      }
    )
    .catch((error) => {
      return {
        props: {
          hasError: error,
        },
      }
    })

  return {
    props: {
      post,
    },
    // Next.js will attempt to re-generate the page:
    // - When a request comes in
    // - At most once every 10 seconds
    revalidate: 10, // In seconds
  }
}

export async function getStaticPaths() {
  try {
    const posts = await api.posts.browse({
      formats: ["html", "plaintext"],
      // page: Number(1),
      limit: 9,
      include: "tags",
      fields: [
        "id",
        "title",
        "slug",
        "excerpt",
        "published_at",
        "feature_image",
        "reading_time",
      ],
    })

    return {
      paths: posts.map((post) => `/blog/${post.slug}`),
      fallback: "blocking",
    }
  } catch (error) {
    return {
      props: {
        hasError: error,
      },
    }
  }
}
